import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { FlexContentContainer } from '../../common/components';
import { HotKeys } from 'react-hotkeys';
import { ChatbotStore, VisualBlocksVisualStore } from '../stores';
import { STORE_PROJECT_VISUAL_BLOCKS, STORE_VISUAL_BLOCKS_CHATBOT } from '../constants';
import { PreviewContent } from '../../common/components/PreviewContent';
import { Button, Layout, List } from 'antd';
import LayoutHeader from '../../../components/LayoutHeader';
import { Utils } from '../../common/services/Utils';
import { isKnownNonImageContentType, normalizeContentType } from '../../common/services/ContentTypeUtils';
import { InteractiveLabelerFilter, Chatbot, VisualBlocksImagePreview } from '../components';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import DocumentPagination from '../../common/components/DocumentPagination';

const { Content } = Layout;
const imageFileNameRegex = /\.(apng|avif|bmp|gif|jpe?g|png|svg|webp)$/i;

export const isImagePackage = (pkg?: { contentType?: string; name?: string } | null): boolean => {
    if (!pkg) {
        return false;
    }

    const contentType = normalizeContentType(pkg.contentType);

    if (contentType.startsWith('image/')) {
        return true;
    }

    if (isKnownNonImageContentType(contentType)) {
        return false;
    }

    return imageFileNameRegex.test(pkg.name || '');
};

type Props = {
    [STORE_PROJECT_VISUAL_BLOCKS]: VisualBlocksVisualStore;
    [STORE_VISUAL_BLOCKS_CHATBOT]: ChatbotStore;
};

export class VisualBlocksPage extends React.Component<Props, object> {
    static map = {
        enableSearch: 'alt+shift+f',
        disableSearch: 'esc'
    };
    private handlers: {};

    constructor(props: Props) {
        super(props);
        this.handlers = {
            enableSearch: this.togglePopUp.bind(this, true),
            disableSearch: this.togglePopUp.bind(this, false)
        };

        this.getContent = this.getContent.bind(this);
    }

    componentDidMount() {
        const store = this.props.projectVisualBlocksUI!;
        const chatbotStore = this.props.visualBlocksChatbotUI!;

        const queryParams = new URLSearchParams(window.location.search);
        const packageId = queryParams.get('package_id');
        const externalWindowMode = queryParams.get('external_window_mode');
        const openAskAlpha = queryParams.get('open_ask_alpha');

        if (packageId) {
            store.setPreSelectedPackageId(packageId);
        }

        if (externalWindowMode === 'true') {
            store.setExternalWindowMode(true);
            store.setLoading(true);
            document.body.classList.add('external-window-mode');
        }

        if (openAskAlpha === 'true') {
            chatbotStore.setDialogVisible(true);
        }
    }

    togglePopUp = (state: boolean) => {
        this.props.projectVisualBlocksUI!.togglePopUp(state);
    };

    getContent = () => {
        const store = this.props.projectVisualBlocksUI;

        if (!store) {
            return null;
        }

        if (isImagePackage(store.selectedPackage)) {
            return <VisualBlocksImagePreview store={store} renderBlocks pageHeaderHeight={270} />;
        }

        const fileExtension = store.selectedPackage && store.selectedPackage.name.split('.').pop();
        if (fileExtension && ['xlsx', 'xls', 'xlsm', 'txt'].indexOf(fileExtension) !== -1) {
            const sortedLines = store.lines.sort(
                (a, b) => (a.coordinates?.page ?? 0) - (b.coordinates?.page ?? 0) || a.rowId - b.rowId
            );
            return (
                <>
                    <List
                        style={{
                            width: 'calc(100vw - 300px)',
                            marginTop: 64,
                            maxHeight: 'calc(100vh - 64px)',
                            overflow: 'auto'
                        }}
                        dataSource={sortedLines}
                        renderItem={item => (
                            <List.Item>
                                <div style={{ whiteSpace: 'pre' }}>{item.text}</div>
                            </List.Item>
                        )}
                    />
                </>
            );
        }

        return (
            <>
                <PreviewContent store={this.props.projectVisualBlocksUI!} renderBlocks pageHeaderHeight={270} />
                {this.props.projectVisualBlocksUI.isPdfLoaded && (
                    <DocumentPagination
                        currentPage={this.props.projectVisualBlocksUI.currentPage + 1}
                        totalPages={this.props.projectVisualBlocksUI.totalPages}
                        onPageChange={page => this.props.projectVisualBlocksUI.setCurrentPage(page - 1)}
                        scale={this.props.projectVisualBlocksUI.scale}
                        onScaleChange={scale => this.props.projectVisualBlocksUI.setScale(scale * 100)}
                        defaultScale={0.7}
                        className="visual-blocks-pagination"
                    />
                )}
            </>
        );
    };

    renderOverlay() {
        return (
            <div className="interactive-labels-loading-overlay">
                <LoadingIndicator />
            </div>
        );
    }

    render() {
        const store = this.props.projectVisualBlocksUI!;
        return (
            <>
                {store.externalWindowMode && store.loading && this.renderOverlay()}
                <HotKeys keyMap={VisualBlocksPage.map} handlers={this.handlers}>
                    <FlexContentContainer
                        content={
                            <>
                                <Layout
                                    className="screen-size"
                                    style={{
                                        height: '100%',
                                        background: 'white',
                                        overflow: 'hidden',
                                        paddingTop: store.externalWindowMode ? 20 : undefined
                                    }}
                                >
                                    <LayoutHeader
                                        subtitle={Utils.getSubtitle(store.project)}
                                        title="Visual Blocks"
                                        helpMessage="Dynamic tools for real-time interaction with labeled documents in project"
                                        buttons={[
                                            <Button
                                                key="clearFilters"
                                                onClick={store.clearFilters}
                                                size="large"
                                                className="light"
                                            >
                                                Clear filters
                                            </Button>
                                        ]}
                                    />
                                    <Layout>
                                        <Content>
                                            <InteractiveLabelerFilter projectVisualBlocksUI={store} />

                                            {this.getContent()}
                                        </Content>
                                    </Layout>

                                    {store.selectedPackage && <Chatbot store={this.props.visualBlocksChatbotUI!} />}
                                </Layout>
                            </>
                        }
                        contentCssClass="unset"
                    />
                </HotKeys>
            </>
        );
    }
}

export default inject(STORE_PROJECT_VISUAL_BLOCKS, STORE_VISUAL_BLOCKS_CHATBOT)(observer(VisualBlocksPage));
